// Pure decision for the builder's SAVE toolbar — which single PRIMARY action to surface and which
// less-frequent actions go under the "Save ▾" menu, based on what's loaded + who owns it. Presentation +
// permission-clarity only (the underlying dialogs/backend are unchanged). Kept pure so the three toolbar
// states are unit-testable without a DOM (scripts/test-save-toolbar-layout.mjs).
//
// Labels (used consistently in the UI):
//   • Update template   — overwrite the currently-loaded template the user OWNS (in place).
//   • Save strategy / Update strategy — create/overwrite the user's own personal strategy record.
//   • Save as a copy    — create a NEW personal strategy record (never overwrites the loaded artifact).
//   • Publish as template — create a categorized community/starter template (always a new, user-owned one).
// "Update template" is shown ONLY when the loaded template is editable by the current user — it must never
// suggest a third-party / starter template can be overwritten.

export type SaveActionKind = 'update-template' | 'save-strategy' | 'save-as-copy' | 'publish-template';
export interface SaveAction { kind: SaveActionKind; label: string; }
export interface SaveToolbarLayout { primary: SaveAction; menu: SaveAction[]; }

export interface SaveToolbarState {
  hasTemplate: boolean;       // a template is loaded (meta.templateId present)
  isTemplateAuthor: boolean;  // the current user owns/can edit that loaded template
  hasStrategyId: boolean;     // a personal strategy record is loaded
}

const SAVE_AS_COPY: SaveAction = { kind: 'save-as-copy', label: 'Save as a copy' };
const PUBLISH: SaveAction = { kind: 'publish-template', label: 'Publish as template' };

export function saveToolbarLayout(s: SaveToolbarState): SaveToolbarLayout {
  // Owned community template → primary overwrites it in place; "Save as a copy" in the menu. Publish is NOT
  // offered here: publishing a NEW/derived template would need explicit derivation provenance (out of
  // scope), so the path is Save as a copy → personal strategy → Publish as template.
  if (s.hasTemplate && s.isTemplateAuthor) {
    return { primary: { kind: 'update-template', label: 'Update template' }, menu: [SAVE_AS_COPY] };
  }
  // Someone else's / starter template → can't overwrite, and NOT publishable yet. Primary makes the user's
  // own copy; Publish only appears once that owned personal copy exists (the normal personal-strategy
  // state). This prevents accidentally republishing another user's template without an owned intermediate
  // copy / explicit derivation provenance.
  if (s.hasTemplate && !s.isTemplateAuthor) {
    return { primary: SAVE_AS_COPY, menu: [] };
  }
  // Plain personal strategy → primary saves/updates it; copy + publish under the menu.
  return {
    primary: { kind: 'save-strategy', label: s.hasStrategyId ? 'Update strategy' : 'Save strategy' },
    menu: [SAVE_AS_COPY, PUBLISH],
  };
}
